import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Receipt, Plus, FileText, Check, Coins, CreditCard, ShoppingBag, Smartphone, Tablet, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Calendar } from "@/components/ui/calendar";
import { useLocalizedDates } from "./i18n/dates";

const REVENUE_FIELDS = ["cash", "card", "wolt", "foodora", "kiosk", "app"];

const FIELD_ICONS = {
  cash: Coins,
  card: CreditCard,
  wolt: ShoppingBag,
  foodora: ShoppingBag,
  kiosk: Tablet,
  app: Smartphone,
};

const FIELD_COLORS = {
  cash: "emerald",
  card: "blue",
  wolt: "blue",
  foodora: "amber",
  kiosk: "zinc",
  app: "zinc",
};

function fieldLabel(key, t) {
  if (key === "cash") return t("daily_closing.field_cash");
  if (key === "card") return t("daily_closing.field_card");
  if (key === "wolt") return "Wolt";
  if (key === "foodora") return "Foodora";
  if (key === "kiosk") return "Kiosk";
  if (key === "app") return "App";
  return t("daily_closing.field_other");
}

function fieldMeta(key, t) {
  return {
    label: fieldLabel(key, t),
    icon: FIELD_ICONS[key] || Coins,
    color: FIELD_COLORS[key] || "zinc",
  };
}

function statusMeta(status, t) {
  switch (status) {
    case "new":
      return { label: t("daily_closing.status_new"), color: "blue" };
    case "verifying":
      return { label: t("daily_closing.status_under_review"), color: "amber" };
    case "approved":
      return { label: t("daily_closing.status_closed"), color: "emerald" };
    case "shortage":
      return { label: t("daily_closing.status_shortage_short"), color: "red" };
    case "shortage_resolved":
      return { label: t("daily_closing.status_shortage_settled"), color: "emerald" };
    default:
      return { label: t("daily_closing.status_new"), color: "blue" };
  }
}

const fmtFt = (v) => `${Math.round(v || 0).toLocaleString("hu-HU")} Ft`;
const fmtIso = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.`;
};
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function PortalDailyClosing() {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(null);
  const [data, setData] = useState({ items: [], pending_total: 0, shortage_total: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [tplRes, listRes] = await Promise.all([
        api.get("/portal/revenue-templates"),
        api.get("/portal/daily-revenues"),
      ]);
      setTemplate((tplRes.data?.items || [])[0] || null);
      setData(listRes.data || { items: [], pending_total: 0 });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabledFields = useMemo(() => {
    if (!template) return [];
    return REVENUE_FIELDS.filter((f) => template.fields?.[f]);
  }, [template]);

  return (
    <div className="space-y-6 px-4 pb-8 pt-6 md:px-6 md:pt-8">
      <header>
        <p className="text-[14px] text-zinc-500">{t("daily_closing.title")}</p>
        <h1 className="mt-1 text-[28px] font-semibold tracking-tight md:text-[32px]">
          {t("daily_closing.new")}
        </h1>
        {template ? (
          <p className="mt-1.5 text-[13px] text-zinc-500">
            {template.site_name} — {t("daily_closing.field_count", { count: enabledFields.length })}
          </p>
        ) : (
          <p className="mt-1.5 text-[13px] text-zinc-500">
            {t("daily_closing.no_template_title")}
          </p>
        )}
      </header>

      {/* Pending hero */}
      <section className="rounded-3xl bg-white p-6 shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_2px_6px_-2px_rgba(0,0,0,0.04)] dark:bg-zinc-900 md:p-7">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 dark:bg-amber-950/40">
          <Receipt className="h-3 w-3 text-amber-600 dark:text-amber-400" strokeWidth={2.2} />
          <span className="text-[11.5px] font-medium text-amber-700 dark:text-amber-300">
            {t("daily_closing.status_pending_approval")}
          </span>
        </div>
        <p className="mt-3 font-mono text-[36px] font-semibold leading-none tabular-nums tracking-[-0.02em] md:text-[44px]">
          {fmtFt(data.pending_total)}
        </p>
        <div className="mt-2 flex items-center gap-2 text-[13px] text-zinc-500">
          <span>{t("daily_closing.in_progress_count", { count: data.items.length })}</span>
          {data.shortage_total > 0 && (
            <>
              <span className="text-zinc-300">·</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                {fmtFt(data.shortage_total)} {t("daily_closing.status_shortage_short").toLowerCase()}
              </span>
            </>
          )}
        </div>

        <button
          type="button"
          data-testid="closing-new"
          onClick={() => setOpen(true)}
          disabled={!template}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-500 py-3 text-[14px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.45)] transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:shadow-none dark:disabled:bg-zinc-700"
        >
          <Plus className="h-4 w-4" strokeWidth={2.5} />
          {t("daily_closing.new")}
        </button>
        {!template && (
          <p className="mt-2 text-center text-[11.5px] text-zinc-400">
            {t("daily_closing.no_template_help")}
          </p>
        )}
      </section>

      {/* List */}
      <section>
        <div className="mb-3 flex items-center justify-between px-1">
          <h2 className="text-[18px] font-semibold tracking-tight">{t("daily_closing.in_progress")}</h2>
          <span className="text-[12px] text-zinc-400">{t("drafts.items_count", { count: data.items.length })}</span>
        </div>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900" />
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <div className="rounded-2xl bg-white p-10 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
            <div className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
              <Check className="h-4 w-4" strokeWidth={2} />
            </div>
            <p className="mt-3 text-[13.5px] text-zinc-500">
              {t("daily_closing.no_progress_entries")}
            </p>
            <p className="mt-1 text-[12px] text-zinc-400">
              {t("daily_closing.approved_hidden")}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.items.map((it) => (
              <RevenueRowCompact
                key={it.id}
                item={it}
                onClick={() => setDetail(it)}
              />
            ))}
          </div>
        )}
      </section>

      <RevenueForm
        open={open}
        onOpenChange={setOpen}
        template={template}
        enabledFields={enabledFields}
        onSaved={() => {
          load();
          setOpen(false);
        }}
      />

      <RevenueDetailDrawer
        item={detail}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}

function RevenueRowCompact({ item, onClick }) {
  const { t } = useTranslation();
  const status = statusMeta(item.status, t);
  const isShortage = item.status === "shortage";
  const statusColors = {
    emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    blue: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
    red: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  };
  return (
    <button
      type="button"
      data-testid={`revenue-${item.id}`}
      onClick={onClick}
      className={
        "flex w-full items-center justify-between gap-3 rounded-2xl px-5 py-4 text-left transition-all " +
        (isShortage
          ? "bg-red-50/60 ring-1 ring-inset ring-red-200 hover:bg-red-50 dark:bg-red-950/20 dark:ring-red-900/40 dark:hover:bg-red-950/30"
          : "bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_-4px_rgba(0,0,0,0.08)] dark:bg-zinc-900")
      }
    >
      <div className="min-w-0 flex-1">
        <p className={"text-[14.5px] font-semibold tracking-tight " + (isShortage ? "text-red-900 dark:text-red-200" : "")}>
          {fmtIso(item.date)}
        </p>
        <span className={"mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium " + (statusColors[status.color] || statusColors.blue)}>
          {isShortage && <span className="h-1 w-1 rounded-full bg-red-500" />}
          {status.label}
          {isShortage && item.shortage_amount > 0 && (
            <span className="ml-0.5 font-mono tabular-nums">−{fmtFt(item.shortage_amount)}</span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <p className={"font-mono text-[16px] font-semibold tabular-nums " + (isShortage ? "text-red-900 dark:text-red-200" : "")}>
          {fmtFt(item.total)}
        </p>
        <ChevronRight className={"h-4 w-4 " + (isShortage ? "text-red-400" : "text-zinc-300")} strokeWidth={2} />
      </div>
    </button>
  );
}

function RevenueDetailDrawer({ item, onClose }) {
  const { t } = useTranslation();
  const status = item ? statusMeta(item.status, t) : null;
  const isShortage = item?.status === "shortage";
  const statusColors = {
    emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    blue: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
    red: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  };
  const fields = item ? Object.entries(item.amounts || {}).filter(([, v]) => v > 0) : [];

  return (
    <Drawer open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent
        data-testid="revenue-detail-drawer"
        className="rounded-t-3xl border-0 bg-white dark:bg-zinc-900"
      >
        <DrawerHeader className="px-5 pb-2 pt-3">
          <DrawerTitle className="text-[20px] font-semibold tracking-tight">
            {item ? fmtIso(item.date) : ""}
          </DrawerTitle>
          {status && (
            <div className="mt-1">
              <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium " + (statusColors[status.color] || statusColors.blue)}>
                {isShortage && <span className="h-1 w-1 rounded-full bg-red-500" />}
                {status.label}
              </span>
            </div>
          )}
        </DrawerHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-5 pb-7 pt-2">
          {/* Shortage banner first if shortage */}
          {isShortage && item.shortages?.length > 0 && (
            <div className="rounded-2xl bg-red-50 p-4 ring-1 ring-red-200 dark:bg-red-950/30 dark:ring-red-900/40">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[12.5px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
                  {t("daily_closing.status_shortage")}
                </p>
                <p className="font-mono text-[20px] font-bold tabular-nums text-red-700 dark:text-red-300">
                  −{fmtFt(item.shortage_amount)}
                </p>
              </div>
              <div className="mt-3 space-y-2">
                {item.shortages.map((s, i) => {
                  const meta = s.type ? fieldMeta(s.type, t) : { label: t("daily_closing.field_other"), icon: FileText };
                  const Icon = meta.icon;
                  return (
                    <div
                      key={s.id || i}
                      className="flex items-start justify-between gap-3 rounded-xl bg-white px-3 py-2.5 dark:bg-zinc-900"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="inline-flex items-center gap-1 text-[12px] font-medium text-zinc-700 dark:text-zinc-300">
                          <Icon className="h-3 w-3 text-red-500" strokeWidth={2} />
                          {meta.label}
                        </p>
                        {s.reason && (
                          <p className="mt-1 text-[11.5px] text-zinc-500">{s.reason}</p>
                        )}
                      </div>
                      <p className="font-mono text-[13.5px] font-semibold tabular-nums text-red-600 dark:text-red-400">
                        −{fmtFt(s.amount)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="rounded-2xl bg-zinc-50 p-4 dark:bg-zinc-800/40">
            <p className="text-[12px] text-zinc-500">{t("daily_closing.reported_amount")}</p>
            <p className="mt-1 font-mono text-[26px] font-semibold leading-none tabular-nums tracking-[-0.02em]">
              {fmtFt(item?.total)}
            </p>
          </div>

          {/* Breakdown */}
          {fields.length > 0 && (
            <div>
              <p className="mb-2 px-2 text-[12.5px] font-medium text-zinc-500">{t("daily_closing.breakdown")}</p>
              <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
                {fields.map(([key, val], i) => {
                  const meta = fieldMeta(key, t);
                  if (!FIELD_ICONS[key]) return null;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={key}
                      className={"flex items-center justify-between gap-3 px-4 py-3 " + (i > 0 ? "border-t border-zinc-100 dark:border-zinc-800/60" : "")}
                    >
                      <div className="inline-flex items-center gap-2">
                        <div className="grid h-8 w-8 place-items-center rounded-lg bg-zinc-100 text-zinc-500 dark:bg-zinc-800/60">
                          <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                        </div>
                        <p className="text-[13.5px] font-medium tracking-tight">{meta.label}</p>
                      </div>
                      <p className="font-mono text-[14.5px] font-semibold tabular-nums">{fmtFt(val)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          {item?.notes && (
            <div>
              <p className="mb-2 px-2 text-[12.5px] font-medium text-zinc-500">{t("daily_closing.notes").replace("…","").replace("(opcionális)","").trim()}</p>
              <div className="rounded-2xl bg-zinc-50 p-4 dark:bg-zinc-800/40">
                <p className="flex items-start gap-2 text-[13px] text-zinc-700 dark:text-zinc-300">
                  <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-400" strokeWidth={2} />
                  {item.notes}
                </p>
              </div>
            </div>
          )}

          {/* Meta */}
          {item?.created_at && (
            <p className="px-2 text-[11.5px] text-zinc-400">
              Rögzítve: {fmtIso(item.created_at.slice(0, 10))}
            </p>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function RevenueForm({ open, onOpenChange, template, enabledFields, onSaved }) {
  const { t } = useTranslation();
  const { dateFnsLocale } = useLocalizedDates();
  const [date, setDate] = useState("");
  const [calOpen, setCalOpen] = useState(false);
  const [amounts, setAmounts] = useState({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeField, setActiveField] = useState(null);

  useEffect(() => {
    if (open) {
      setDate("");
      setAmounts({});
      setNotes("");
      setActiveField(enabledFields[0] || null);
      setCalOpen(false);
    }
  }, [open, enabledFields]);

  const setAmount = (k, raw) => {
    const clean = String(raw).replace(/\D/g, "");
    setAmounts((a) => ({ ...a, [k]: clean }));
  };
  const displayAmount = (raw) => {
    if (!raw) return "";
    return String(raw).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
  };
  const numericAmount = (raw) => {
    if (!raw) return 0;
    const n = parseInt(String(raw), 10);
    return isNaN(n) ? 0 : n;
  };

  // Custom keypad handlers
  const keypadInput = (key) => {
    if (!activeField) return;
    if (key === "del") {
      const cur = amounts[activeField] || "";
      setAmount(activeField, cur.slice(0, -1));
    } else if (key === "clear") {
      setAmount(activeField, "");
    } else {
      const cur = amounts[activeField] || "";
      const next = (cur + key).slice(0, 12); // safety cap
      setAmount(activeField, next.replace(/^0+(\d)/, "$1"));
    }
  };

  const total = useMemo(
    () => enabledFields.reduce((sum, f) => sum + numericAmount(amounts[f]), 0),
    [amounts, enabledFields],
  );

  const onSubmit = async () => {
    if (!template) return;
    if (!date) {
      toast.error(t("schedule.pick_date"));
      return;
    }
    setSubmitting(true);
    try {
      const numericAmounts = {};
      enabledFields.forEach((f) => {
        numericAmounts[f] = numericAmount(amounts[f]);
      });
      await api.post("/portal/daily-revenues", {
        template_id: template.id,
        date,
        amounts: numericAmounts,
        notes: notes || null,
      });
      toast.success(t("daily_closing.title"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const dateLabel = (() => {
    if (!date) return null;
    const d = new Date(date + "T00:00:00");
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.`;
  })();

  const activeMeta = activeField ? fieldMeta(activeField, t) : null;
  const activeValue = activeField ? amounts[activeField] : "";

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-testid="closing-form-drawer"
        className="flex h-[94dvh] flex-col rounded-t-3xl border-0 bg-zinc-50 p-0 dark:bg-zinc-950"
      >
        {/* Header */}
        <DrawerHeader className="shrink-0 px-5 pb-1 pt-3 text-left">
          <DrawerTitle className="text-[17px] font-semibold tracking-tight text-zinc-900 dark:text-white">
            {t("daily_closing.new")}
          </DrawerTitle>
          {template && (
            <p className="mt-0.5 text-[12.5px] text-zinc-500">{template.site_name}</p>
          )}
        </DrawerHeader>

        {/* Hero: active amount display (Apple Pay style) */}
        <div className="shrink-0 px-5 pt-2">
          {activeField && activeMeta ? (
            <div className="rounded-2xl bg-white px-5 py-5 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
              <p className="inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-wide text-zinc-400">
                <activeMeta.icon className="h-3 w-3" strokeWidth={2.2} />
                {activeMeta.label}
              </p>
              <div className="mt-1 flex items-baseline gap-2">
                <p className={"font-mono text-[42px] font-semibold leading-none tabular-nums tracking-[-0.03em] " + (activeValue ? "text-zinc-900 dark:text-white" : "text-zinc-300 dark:text-zinc-700")}>
                  {displayAmount(activeValue) || "0"}
                </p>
                <span className="text-[16px] font-medium text-zinc-400">Ft</span>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl bg-white px-5 py-5 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
              <p className="text-[12px] font-medium uppercase tracking-wide text-zinc-400">{t("daily_closing.total")}</p>
              <div className="mt-1 flex items-baseline gap-2">
                <p className={"font-mono text-[42px] font-semibold leading-none tabular-nums tracking-[-0.03em] " + (total > 0 ? "text-zinc-900 dark:text-white" : "text-zinc-300 dark:text-zinc-700")}>
                  {total.toLocaleString("hu-HU").replace(/,/g, "\u00a0")}
                </p>
                <span className="text-[16px] font-medium text-zinc-400">Ft</span>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable rows */}
        <div className="flex-1 overflow-y-auto px-5 pb-3 pt-3">
          {/* Date row */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
            <button
              type="button"
              data-testid="closing-date"
              onClick={() => { setCalOpen((v) => !v); setActiveField(null); }}
              className="flex h-12 w-full items-center gap-3 px-4 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
            >
              <CalendarDays className={"h-4 w-4 " + (calOpen ? "text-blue-500" : "text-zinc-400")} strokeWidth={2} />
              <span className="flex-1 text-[14px] font-medium text-zinc-700 dark:text-zinc-200">{t("daily_closing.date")}</span>
              <span className={dateLabel ? "font-mono text-[13.5px] tabular-nums text-zinc-900 dark:text-white" : "text-[13.5px] text-zinc-400"}>
                {dateLabel || t("common.pick")}
              </span>
              <ChevronRight className={"h-4 w-4 text-zinc-300 transition-transform " + (calOpen ? "rotate-90 text-blue-500" : "")} strokeWidth={2} />
            </button>
            {calOpen && (
              <div data-testid="closing-date-popover" className="border-t border-zinc-100 bg-zinc-50/60 px-2 py-2 dark:border-zinc-800 dark:bg-zinc-800/30">
                <Calendar
                  mode="single"
                  locale={dateFnsLocale}
                  weekStartsOn={1}
                  selected={date ? new Date(date + "T00:00:00") : undefined}
                  onDayClick={(d) => {
                    if (!d) return;
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    setDate(iso);
                    setCalOpen(false);
                  }}
                  classNames={{
                    months: "flex flex-col gap-2",
                    caption: "flex items-center justify-center pt-1 relative px-1",
                    caption_label: "text-[13.5px] font-semibold capitalize text-zinc-900 dark:text-white",
                    nav: "flex items-center gap-1",
                    nav_button: "h-8 w-8 grid place-items-center rounded-full text-zinc-500 hover:bg-white hover:text-zinc-900 transition dark:hover:bg-zinc-700 dark:hover:text-white",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse",
                    head_row: "flex w-full justify-between",
                    head_cell: "w-10 text-center text-[10.5px] font-medium uppercase text-zinc-400",
                    row: "flex w-full justify-between mt-1",
                    cell: "w-10 h-9 text-center p-0",
                    day: "h-9 w-9 inline-flex items-center justify-center rounded-full text-[13px] font-medium tabular-nums text-zinc-700 hover:bg-white focus:outline-none dark:text-zinc-200 dark:hover:bg-zinc-700",
                    day_selected: "bg-blue-500 text-white shadow-[0_4px_12px_-4px_rgba(59,130,246,0.5)] hover:bg-blue-500 hover:text-white",
                    day_today: "ring-2 ring-blue-500 ring-inset",
                    day_outside: "text-zinc-300 dark:text-zinc-700",
                    day_disabled: "opacity-40",
                    day_hidden: "invisible",
                  }}
                />
              </div>
            )}
          </div>

          {/* Amount rows */}
          {enabledFields.length === 0 ? (
            <p className="mt-3 rounded-2xl bg-white p-4 text-center text-[12.5px] text-zinc-500 dark:bg-zinc-900">
              {t("daily_closing.no_active_field")}
            </p>
          ) : (
            <div className="mt-3 overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
              {enabledFields.map((f, i) => {
                const meta = fieldMeta(f, t);
                const Icon = meta.icon;
                const isActive = activeField === f && !calOpen;
                return (
                  <button
                    key={f}
                    type="button"
                    data-testid={`closing-${f}`}
                    onClick={() => { setActiveField(f); setCalOpen(false); }}
                    className={
                      "flex h-12 w-full items-center gap-3 px-4 text-left transition-colors " +
                      (i > 0 ? "border-t border-zinc-100 dark:border-zinc-800/60 " : "") +
                      (isActive ? "bg-blue-50/60 dark:bg-blue-950/30" : "hover:bg-zinc-50 dark:hover:bg-zinc-800/60")
                    }
                  >
                    <Icon className={"h-4 w-4 " + (isActive ? "text-blue-500" : "text-zinc-400")} strokeWidth={2} />
                    <span className={"flex-1 text-[14px] font-medium " + (isActive ? "text-blue-700 dark:text-blue-300" : "text-zinc-700 dark:text-zinc-200")}>
                      {meta.label}
                    </span>
                    <span className={"font-mono text-[14.5px] tabular-nums " + (amounts[f] ? "font-semibold text-zinc-900 dark:text-white" : "text-zinc-300")}>
                      {displayAmount(amounts[f]) || "0"}
                    </span>
                    <span className="text-[11.5px] font-medium text-zinc-400">Ft</span>
                  </button>
                );
              })}
              {/* Total inline */}
              <div className="flex h-12 items-center gap-3 border-t border-zinc-100 bg-zinc-50/70 px-4 dark:border-zinc-800/60 dark:bg-zinc-800/30">
                <span className="flex-1 text-[13px] font-semibold uppercase tracking-wide text-zinc-500">
                  {t("common.total")}
                </span>
                <span className="font-mono text-[15px] font-semibold tabular-nums text-zinc-900 dark:text-white">
                  {fmtFt(total)}
                </span>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="mt-3 overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
            <textarea
              rows={2}
              data-testid="closing-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onFocus={() => { setActiveField(null); setCalOpen(false); }}
              placeholder={t("daily_closing.notes")}
              className="block w-full resize-none bg-transparent px-4 py-3 text-[16px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:text-white"
            />
          </div>
        </div>

        {/* Fixed footer: keypad (if active) OR submit button */}
        <div className="shrink-0 border-t border-zinc-200/60 bg-white/95 px-4 pb-4 pt-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/95">
          {activeField && !calOpen ? (
            <Keypad
              onInput={keypadInput}
              onDone={() => {
                const idx = enabledFields.indexOf(activeField);
                if (idx >= 0 && idx < enabledFields.length - 1) {
                  setActiveField(enabledFields[idx + 1]);
                } else {
                  setActiveField(null);
                }
              }}
              isLast={enabledFields.indexOf(activeField) === enabledFields.length - 1}
            />
          ) : (
            <>
              <button
                type="button"
                data-testid="closing-submit"
                onClick={onSubmit}
                disabled={submitting || !template || enabledFields.length === 0 || !date}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-500 text-[14.5px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.5)] transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-400 disabled:shadow-none dark:disabled:bg-zinc-800 dark:disabled:text-zinc-600"
              >
                {submitting ? t("common.saving") : <><Check className="h-4 w-4" strokeWidth={2.5} /> {t("daily_closing.submit")}</>}
              </button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="mt-1.5 inline-flex h-10 w-full items-center justify-center rounded-2xl text-[13.5px] font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
              >
                {t("common.cancel")}
              </button>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function Keypad({ onInput, onDone, isLast }) {
  const { t } = useTranslation();
  // iOS-style numeric keypad
  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["000", "0", "del"],
  ];
  return (
    <div data-testid="keypad" className="space-y-2">
      <div className="grid grid-cols-3 gap-1.5">
        {keys.flat().map((k) => (
          <button
            key={k}
            type="button"
            data-testid={`keypad-${k}`}
            onClick={() => onInput(k)}
            className={
              "h-[52px] rounded-xl text-[22px] font-medium tabular-nums shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-all active:scale-[0.96] active:bg-zinc-200 " +
              (k === "del"
                ? "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                : k === "000"
                ? "bg-white text-zinc-900 text-[18px] font-semibold dark:bg-zinc-800/80 dark:text-white"
                : "bg-white text-zinc-900 dark:bg-zinc-800/80 dark:text-white")
            }
          >
            {k === "del" ? "⌫" : k}
          </button>
        ))}
      </div>
      <button
        type="button"
        data-testid="keypad-done"
        onClick={onDone}
        className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-blue-500 text-[14px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.5)] transition hover:bg-blue-600"
      >
        {isLast ? t("common.done") : t("common.next")}
      </button>
    </div>
  );
}
